/**
 * The `bear` capability: what a jig can do with the project's model.
 *
 * The live `Model` and `q()` stay in the Bear sandbox iframe. A jig asks for
 * serialized nodes the same way the BOM and parts list panes do, and drives the
 * 3D view with the same commands.
 */
import type { QueryStep } from "bear/engine/query-plan";
import type { CapabilityProvider } from "playground/apps/capabilities";
import { invariant } from "ts-invariant";

import type { RunResult } from "@/components/BearSandboxProvider";
import {
  currentSelection,
  frameNodes,
  highlightNodes,
  queryGraph,
  selectNodes,
  unhighlightNodes,
} from "@/lib/bear-sandbox-bridge";

// The outer shape only: the sandbox's plan runner rejects an unknown step.
function isPlan(value: unknown): value is QueryStep[] {
  return (
    Array.isArray(value) &&
    value.every((s) => Array.isArray(s) && typeof s[0] === "string")
  );
}

function nodeIds(value: unknown): string[] {
  invariant(
    Array.isArray(value) && value.every((id) => typeof id === "string"),
    "expected an array of node ids",
  );
  return value;
}

export function bearCapability(
  run: () => Promise<RunResult>,
): CapabilityProvider {
  return {
    /** Rebuild the model from the project files; resolves to its errors. */
    run: async () => (await run()).errors,
    query: (query) => {
      invariant(isPlan(query), "expected a query plan: an array of steps");
      return queryGraph(query);
    },
    selection: () => currentSelection(),
    select: (ids) => selectNodes(nodeIds(ids)),
    highlight: (ids) => highlightNodes(nodeIds(ids)),
    unhighlight: (ids) => unhighlightNodes(nodeIds(ids)),
    frame: (ids) => frameNodes(nodeIds(ids)),
  };
}
